import { PHOENIX_EVENT_CONTEXT, withPhoenixEventContext } from '@/analytics/phoenixContext'
import { type AnalyticsOptions, getAnalyticsClients } from '@/plugins/analyticsClients'
import { useAnalyticsSessionStore } from '@/stores/analyticsSession'
import type { AnalyticEvents } from '@/types/analytics'
import { withTimeout } from '@/utils/withTimeout'
import type { User } from '@segment/analytics-next'
import type { PageParams } from '@segment/analytics-next/dist/types/core/arguments-resolver'
import snakecaseKeys from 'snakecase-keys'

const SEGMENT_ANONYMOUS_USER_ID_KEY = 'ajs_anonymous_id'

const safely = (callback: () => unknown): void => {
  try {
    callback()
  } catch {
    // Analytics failures (for example ad blockers) should never break account flows.
  }
}

const transformEvent = (eventData?: Record<string, unknown> | null): Record<string, unknown> => {
  // CA has historically snake_cased Segment payloads, so the shared facade keeps that warehouse contract for both sinks.
  return snakecaseKeys(eventData ?? {})
}

const getOrCreateAnonymousId = (): string => {
  const existingAnonymousId = localStorage.getItem(SEGMENT_ANONYMOUS_USER_ID_KEY)

  if (existingAnonymousId) {
    return existingAnonymousId
  }

  const uuid = crypto.randomUUID()
  localStorage.setItem(SEGMENT_ANONYMOUS_USER_ID_KEY, uuid)

  return uuid
}

export type IdentifyTraits = {
  email: string
  first_name: string
  last_name: string
  phone: string
  tenant: string
  cognito_user_id: string
}

export const useAnalytics = () => {
  const track = <K extends keyof AnalyticEvents>(
    eventName: K,
    eventData?: AnalyticEvents[K],
    options?: AnalyticsOptions,
  ): void => {
    const { customerio, segment } = getAnalyticsClients()
    const transformedData = transformEvent(withPhoenixEventContext(eventData as Record<string, unknown> | null | undefined))

    safely(() => customerio?.track(eventName, transformedData, options))
    safely(() => segment?.track(eventName, transformedData, options))
  }

  // Fires an event at most once per page session. dedupKey scopes uniqueness —
  // e.g. order_id for Order Viewed, animal_id for Dog Profile Viewed.
  // Omit dedupKey to fire an event at most once per session regardless of payload.
  const trackOncePerSession = <K extends keyof AnalyticEvents>(
    eventName: K,
    eventData?: AnalyticEvents[K],
    dedupKey?: string,
  ): void => {
    const session = useAnalyticsSessionStore()
    const compositeKey = dedupKey ? `${String(eventName)}:${dedupKey}` : String(eventName)

    if (session.hasFiredInSession(compositeKey)) {
      return
    }

    session.markFiredInSession(compositeKey)
    track(eventName, eventData)
  }

  const identify = (traits: IdentifyTraits): void => {
    const { customerio, segment } = getAnalyticsClients()
    const traitsWithContext = { ...PHOENIX_EVENT_CONTEXT, ...traits }

    // Customer.io keys users by email (legacy CA behaviour). Segment keeps Cognito for identity continuity.
    safely(() => customerio?.identify(traits.email, traitsWithContext))
    safely(() => segment?.identify(traits.cognito_user_id, traitsWithContext))
  }

  const page = (...args: PageParams): void => {
    const { customerio, segment } = getAnalyticsClients()

    if (args.length === 1 && typeof args[0] === 'string') {
      const properties = transformEvent(PHOENIX_EVENT_CONTEXT)
      safely(() => customerio?.page(args[0], properties))
      safely(() => segment?.page(args[0], properties))
      return
    }

    safely(() => customerio?.page(...args))
    safely(() => segment?.page(...args))
  }

  const reset = (): void => {
    const { customerio, segment } = getAnalyticsClients()

    // Clear per-session dedup so a freshly-logged-in user starts a new session.
    useAnalyticsSessionStore().resetSession()
    safely(() => customerio?.reset())
    safely(() => segment?.reset())
  }

  const trackLink = (
    element: HTMLAnchorElement,
    eventName: keyof AnalyticEvents,
    eventData?: Record<string, unknown>,
    options?: AnalyticsOptions,
  ): void => {
    const { customerio, segment } = getAnalyticsClients()
    const transformedData = transformEvent(withPhoenixEventContext(eventData))

    safely(() => customerio?.trackLink?.(element, eventName, transformedData, options))
    safely(() => segment?.trackLink?.(element, eventName, transformedData, options))
  }

  const getUser = async (): Promise<User | undefined> => {
    try {
      const { segment } = getAnalyticsClients()

      const user = segment?.user

      if (!user) {
        return undefined
      }

      return await withTimeout<User>(user, 2_000)
    } catch {
      return undefined
    }
  }

  const anonymousId = async (): Promise<string> => {
    const user = await getUser()
    const id = user?.anonymousId()

    if (id) {
      return id
    }

    return getOrCreateAnonymousId()
  }

  return {
    anonymousId,
    getAnonymousId: anonymousId,
    identify,
    page,
    reset,
    track,
    trackLink,
    trackOncePerSession,
  }
}
